import axios from 'axios';
import { normalizeApiError } from './errors';
import { buildAuthHeaders, clearStoredAuthSession, getStoredAuthToken } from './authStorage';

const PREVIEW_HOST_PATTERN = /(^|\.)(preview\.emergentagent\.com|emergent\.host)$/;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const normalizeBaseUrl = (value) => String(value || '').trim().replace(/\/+$/, '');

const isPreviewHost = (hostname = '') => PREVIEW_HOST_PATTERN.test(hostname);
const isLocalHostname = (hostname = '') => LOCAL_HOSTNAMES.has(hostname);

export const resolveApiBaseUrl = () => {
  const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);

  if (typeof window === 'undefined') {
    return configuredBaseUrl;
  }

  const currentOrigin = normalizeBaseUrl(window.location.origin);
  if (!configuredBaseUrl) {
    return currentOrigin;
  }

  try {
    const configuredUrl = new URL(configuredBaseUrl);
    const currentUrl = new URL(currentOrigin);
    const configuredIsPreview = isPreviewHost(configuredUrl.hostname);
    const currentIsPreview = isPreviewHost(currentUrl.hostname);

    if (!currentIsPreview && !isLocalHostname(currentUrl.hostname) && configuredIsPreview && configuredUrl.origin !== currentUrl.origin) {
      console.warn('[api] REACT_APP_BACKEND_URL aponta para preview enquanto a app corre num domínio custom. A usar origin actual.', {
        configuredBaseUrl,
        currentOrigin,
      });
      return currentOrigin;
    }
  } catch (error) {
    console.warn('[api] REACT_APP_BACKEND_URL inválido. A usar valor configurado como está.', error);
  }

  return configuredBaseUrl;
};

export const API_BASE_URL = resolveApiBaseUrl();
export const API_URL = API_BASE_URL + '/api';

const apiRequestCache = new Map();
const apiRequestInflight = new Map();

const cloneCachePayload = (value) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
};

const buildCachedAxiosSnapshot = (response) => ({
  data: cloneCachePayload(response?.data),
  status: response?.status,
  statusText: response?.statusText,
  headers: response?.headers,
  config: response?.config,
});

const getAuthScopedCacheKey = (key) => {
  const token = getStoredAuthToken();
  const tokenScope = token ? token.slice(-24) : 'anonymous';
  return `${key}::${tokenScope}`;
};

const getCachedApiGet = async (key, requestFactory, ttlMs) => {
  const scopedKey = getAuthScopedCacheKey(key);
  const now = Date.now();
  const cached = apiRequestCache.get(scopedKey);

  if (cached && cached.expiresAt > now) {
    return buildCachedAxiosSnapshot(cached.response);
  }

  if (apiRequestInflight.has(scopedKey)) {
    const inflightResponse = await apiRequestInflight.get(scopedKey);
    return buildCachedAxiosSnapshot(inflightResponse);
  }

  const requestPromise = requestFactory()
    .then((response) => {
      apiRequestCache.set(scopedKey, {
        response,
        expiresAt: Date.now() + ttlMs,
      });
      return response;
    })
    .finally(() => {
      apiRequestInflight.delete(scopedKey);
    });

  apiRequestInflight.set(scopedKey, requestPromise);
  const response = await requestPromise;
  return buildCachedAxiosSnapshot(response);
};

const getHeaderValue = (headers, key) => {
  if (!headers || !key) return null;
  if (typeof headers.get === 'function') {
    return headers.get(key) || headers.get(String(key).toLowerCase()) || null;
  }
  const normalizedKey = String(key).toLowerCase();
  const directMatch = Object.entries(headers || {}).find(([headerKey]) => String(headerKey).toLowerCase() === normalizedKey);
  return directMatch ? directMatch[1] : null;
};

const buildRequestMeta = (config = {}, response = null, error = null) => {
  const startedAt = Number(config?.metadata?.startedAt || 0);
  const finishedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : Date.now();
  const durationMs = startedAt ? Math.round(finishedAt - startedAt) : null;
  const requestId = getHeaderValue(response?.headers || error?.response?.headers, 'x-request-id');

  return {
    method: String(config?.method || 'get').toUpperCase(),
    url: String(config?.url || ''),
    durationMs,
    requestId: requestId || null,
    status: response?.status || error?.response?.status || null,
  };
};

const buildBlobRequestError = async (response, fallbackMessage) => {
  const contentType = response.headers.get('content-type') || '';
  let detail = '';

  try {
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      detail = payload?.detail || payload?.message || '';
    } else {
      detail = (await response.text())?.trim?.() || '';
    }
  } catch {
    detail = '';
  }

  const error = new Error(detail || fallbackMessage || `HTTP ${response.status}`);
  error.response = {
    status: response.status,
    data: {
      detail: detail || fallbackMessage || `HTTP ${response.status}`,
    },
  };
  return error;
};

const fetchBlobWithAuth = async (path, fallbackMessage = 'Erro ao carregar ficheiro') => {
  const response = await fetch(`${API_URL}${path}`, {
    headers: buildAuthHeaders(),
  });

  if (!response.ok) {
    throw await buildBlobRequestError(response, fallbackMessage);
  }

  return {
    data: await response.blob(),
    headers: {
      'content-type': response.headers.get('content-type') || '',
    },
  };
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    config.metadata = {
      ...(config.metadata || {}),
      startedAt: (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : Date.now(),
    };
    config.headers = buildAuthHeaders(config.headers || {});
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    response.__requestMeta = buildRequestMeta(response.config, response, null);
    return response;
  },
  (error) => {
    const requestUrl = String(error.config?.url || '');
    const shouldForceLogin = error.response?.status === 401 && !requestUrl.includes('/auth/login');

    error.__requestMeta = buildRequestMeta(error.config, null, error);

    if (shouldForceLogin) {
      clearStoredAuthSession();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Utility function to extract error message from API response
export const getErrorMessage = (error, defaultMessage = 'Ocorreu um erro', options = {}) => {
  return normalizeApiError(error, defaultMessage, options);
};

export default api;

// Alias for products API (compatibility)
export const productsAPI = {
  list: (params) => api.get('/inventory/products', { params }),
  get: (id) => api.get(`/inventory/products/${id}`),
  create: (data) => api.post('/inventory/products', data),
  update: (id, data) => api.put(`/inventory/products/${id}`, data),
};

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getPublicLoginStats: () => api.get('/auth/public-login-stats'),
  register: (userData) => api.post('/auth/register', userData),
  createAdmin: () => api.post('/auth/create-admin'),
  getSession: () => getCachedApiGet('auth.session', () => api.get('/auth/session'), 60000),
  refreshSession: () => api.get('/auth/session'),
  forgotPassword: (payload) => api.post('/auth/forgot-password', payload),
  validateResetToken: (token) => api.get('/auth/reset-password/validate', { params: { token } }),
  resetPassword: (payload) => api.post('/auth/reset-password', payload),
};

export const aiEngineeringAPI = {
  getControlPlane: () => getCachedApiGet('ai.control-plane', () => api.get('/ai/config'), 60000),
  getFeatureFlag: () => getCachedApiGet('ai.feature-flag', () => api.get('/ai/feature-flag'), 60000),
  updateFeatureFlag: (payload) => api.put('/ai/feature-flag', payload),
  updateControlPlane: (payload) => api.put('/ai/config/settings', payload),
  updateCredentialOverride: (payload) => api.put('/ai/config/credential-override', payload),
  clearCredentialOverride: () => api.delete('/ai/config/credential-override'),
  listControlPlaneAudit: () => api.get('/ai/config/audit'),
  previewCredentialResolution: (payload) => api.post('/ai/config/credential-resolution-preview', payload),
  listTasks: () => api.get('/ai/tasks'),
  getTask: (taskId) => api.get(`/ai/tasks/${taskId}`),
  createTask: (payload) => api.post('/ai/tasks', payload),
  analyzeTask: (taskId) => api.post(`/ai/tasks/${taskId}/analyze`),
  generatePatch: (taskId) => api.post(`/ai/tasks/${taskId}/generate-patch`),
  runTests: (taskId) => api.post(`/ai/tasks/${taskId}/run-tests`),
  approveTask: (taskId) => api.post(`/ai/tasks/${taskId}/approve`),
  rejectTask: (taskId, payload) => api.post(`/ai/tasks/${taskId}/reject`, payload),
  openPr: (taskId) => api.post(`/ai/tasks/${taskId}/open-pr`),
};

export const manualAPI = {
  getInfo: () => getCachedApiGet('manual.info', () => api.get('/manual/info'), 300000),
  listReleases: () => api.get('/manual/releases'),
  publishRelease: (payload) => api.post('/manual/releases/publish', payload),
  updateCurrentRelease: (payload) => api.put('/manual/releases/current', payload),
  downloadProfileSop: (roleKey) => api.get(`/manual/download/profile/${roleKey}`, { responseType: 'blob' }),
  listTrainingDocuments: (params = {}) => api.get('/manual/training-documents', { params }),
  getTrainingDashboard: () => api.get('/manual/training-documents/dashboard'),
  uploadTrainingDocument: (formData) => api.post('/manual/training-documents/upload', formData, {
    headers: buildAuthHeaders(),
  }),
  setTrainingDocumentPublication: (documentId, versionId, isPublished) => api.put(`/manual/training-documents/${documentId}/versions/${versionId}/publication`, {
    is_published: isPublished,
  }),
  openTrainingDocument: (documentId, versionId, disposition = 'inline') => fetchBlobWithAuth(`/manual/training-documents/${documentId}/versions/${versionId}/file?disposition=${encodeURIComponent(disposition)}`, 'Não foi possível abrir o documento'),
  acceptTrainingDocument: (documentId, versionId) => api.post(`/manual/training-documents/${documentId}/versions/${versionId}/accept`),
  exportTrainingReport: () => fetchBlobWithAuth('/manual/training-documents/report/export', 'Não foi possível exportar o relatório'),
};

export const inventoryAPI = {
  getSummary: () => getCachedApiGet('inventory.summary', () => api.get('/inventory/summary'), 30000),
  listProducts: (includeDeleted = false) => api.get('/inventory/products', { params: { include_deleted: includeDeleted } }),
  previewSku: (params = {}) => api.get('/inventory/products/sku-preview', { params }),
  listPosAvailability: (params = {}) => api.get('/inventory/products/pos-availability', { params }),
  listDeletedProducts: () => api.get('/inventory/products/deleted'),
  getProduct: (productReference) => api.get(`/inventory/products/${encodeURIComponent(productReference)}`),
  createProduct: (data) => api.post('/inventory/products', data),
  updateProduct: (id, data) => api.put(`/inventory/products/${id}`, data),
  // New: Edit with mandatory justification
  editProduct: (id, data, justification) => api.put(`/inventory/products/${id}/edit`, { ...data, justification }),
  resolveUnallocatedStock: (id, locationId) => api.post(`/inventory/products/${id}/resolve-unallocated-stock`, null, { params: { location_id: locationId } }),
  // New: Soft delete with mandatory reason
  deleteProduct: (id, deleteReason) => api.delete(`/inventory/products/${id}`, { data: { delete_reason: deleteReason } }),
  // New: Restore deleted product
  restoreProduct: (id) => api.post(`/inventory/products/${id}/restore`),
  bulkStatus: (data) => api.post('/inventory/products/bulk-status', data),
  bulkDelete: (data) => api.post('/inventory/bulk-delete', data),
  downloadBulkUpdateTemplate: (params = {}) => api.get('/inventory/bulk-update/template', { params, responseType: 'blob' }),
  importBulkUpdateFile: (formData, previewOnly = false) => {
    if (previewOnly) {
      formData.append('preview_only', 'true');
    }
    return api.post('/inventory/bulk-update/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // New: Audit log
  getProductAudit: (id) => api.get(`/inventory/products/${id}/audit`),
  getAllAuditLogs: (limit = 100) => api.get('/inventory/audit/all', { params: { limit } }),
  adjustStock: (productId, quantity, reason, locationId = null) => 
    api.post('/inventory/adjust-stock', null, { params: { product_id: productId, quantity, reason, location_id: locationId || undefined } }),
  getLowStock: () => api.get('/inventory/low-stock'),
  getMovements: (productId) => api.get('/inventory/movements', { params: { product_id: productId } }),
  
  // Product Images - Multi-image support
  uploadProductImages: (productId, files, setMain = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (setMain) formData.append('set_main', setMain);
    return api.post(`/attachments/product-images/${productId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  setMainProductImage: (productId, imageUrl) => 
    api.put(`/attachments/product-images/${productId}/main`, { image_url: imageUrl }),
  deleteProductImage: (productId, imageUrl) => 
    api.delete(`/attachments/product-images/${productId}`, { data: { image_url: imageUrl } }),
};

export const stockTransfersAPI = {
  create: (data) => api.post('/stock/transfers', data),
  update: (id, data) => api.put(`/stock/transfers/${id}`, data),
  list: (params = {}) => api.get('/stock/transfers', { params }),
  get: (id) => api.get(`/stock/transfers/${id}`),
  availability: (productId, originLocationId, destinationLocationId = null) => api.get('/stock/transfers/availability', {
    params: {
      product_id: productId,
      origin_location_id: originLocationId,
      destination_location_id: destinationLocationId,
    },
  }),
  submit: (id) => api.post(`/stock/transfers/${id}/submit`),
  approve: (id) => api.post(`/stock/transfers/${id}/approve`),
  cancel: (id, reason) => api.post(`/stock/transfers/${id}/cancel`, { reason }),
  delete: (id) => api.delete(`/stock/transfers/${id}`),
  bulkStatus: (data) => api.post('/stock/transfers/bulk-status', data),
};

export const itemsAPI = {
  list: (params) => api.get('/items/', { params }),
  listDeleted: () => api.get('/items/deleted'),
  get: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items/', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  // New: Edit with mandatory justification
  editItem: (id, data, justification) => api.put(`/items/${id}/edit`, { ...data, justification }),
  // New: Soft delete with mandatory reason
  deleteItem: (id, deleteReason) => api.delete(`/items/${id}`, { data: { delete_reason: deleteReason } }),
  // New: Restore deleted item
  restoreItem: (id) => api.post(`/items/${id}/restore`),
  // New: Audit log
  getItemAudit: (id) => api.get(`/items/${id}/audit`),
  getAllAuditLogs: (limit = 100) => api.get('/items/audit/all', { params: { limit } }),
  getMovements: (id) => api.get(`/items/${id}/movements`),
  // Updated: Transfer with location_id
  transfer: (itemId, toLocationId, notes) => 
    api.post('/items/transfer', { item_id: itemId, to_location_id: toLocationId, notes }),
  // New: Batch transfer multiple items
  batchTransfer: (itemIds, toLocationId, notes) =>
    api.post('/items/transfer/batch', { item_ids: itemIds, to_location_id: toLocationId, notes }),
  searchBySerial: (query) => api.get('/items/search/by-serial', { params: { query } }),
  // Updated: location_id parameter for filtering by location
  getAvailableForSale: (productId, locationId) => 
    api.get('/items/available-for-sale', { params: { product_id: productId, location_id: locationId } }),
  registerInventory: (itemId, notes) => 
    api.post(`/items/${itemId}/register-inventory`, null, { params: { notes } }),
  // Serial Numbers CRUD
  getSerialNumber: (itemId) => api.get(`/items/${itemId}/serial-numbers`),
  getProductSerialNumbers: (productId, params) => 
    api.get(`/items/product/${productId}/serial-numbers`, { params }),
  updateSerialNumber: (itemId, data) => api.put(`/items/${itemId}/serial-number`, data),
  deleteSerialNumber: (itemId, reason) => 
    api.delete(`/items/${itemId}/serial-number`, { params: { reason } }),
  addSerialNumber: (productId, data, location) => 
    api.post(`/items/product/${productId}/serial-number`, data, { params: { location } }),
};

export const serialNumbersAPI = {
  list: (params) => api.get('/serial-numbers', { params }),
  move: (itemId, locationId, reason) => api.put(`/serial-numbers/${itemId}/move`, { location_id: locationId, reason }),
  remove: (itemId, reason) => api.delete(`/serial-numbers/${itemId}`, { data: { reason } }),
  getAudit: (itemId = null, limit = 100) => api.get('/serial-numbers/audit', { params: { item_id: itemId, limit } })
};

export const categoriesAPI = {
  list: () => api.get('/categories/'),
  getSchema: (categoryKey) => api.get(`/categories/${categoryKey}/schema`),
  getAllSchemas: () => api.get('/categories/all-schemas'),
  validateSpecs: (category, specs, extra = {}) => api.post('/categories/validate-specs', { category, specs, ...extra }),
  getStats: () => api.get('/categories/schema-stats'),
};

export const usersAPI = {
  list: () => api.get('/users/'),
  get: (id) => api.get(`/users/${id}`),
  getMe: () => api.get('/users/me'),
  updateMyLanguagePreference: (preferredLanguage) => api.put('/users/me/preferences/language', { preferred_language: preferredLanguage }),
  updateMyWelcomeTourPreference: (completed) => api.put('/users/me/preferences/welcome-tour', { completed }),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id, reason = null) => api.delete(`/users/${id}`, { data: { reason } }),
  bulkStatus: (data) => api.post('/users/bulk-status', data),
  exportUsersXlsx: (params) => api.get('/users/export/xlsx', { params, responseType: 'blob' }),
  resetPassword: (id, newPassword, justification) => 
    api.post(`/users/${id}/reset-password`, { new_password: newPassword, justification }),
  getAuditLog: (userId) => api.get(`/users/${userId}/audit-log`),
  getAllAuditLogs: (limit = 100) => api.get('/users/audit-log/all', { params: { limit } }),
  getRoles: () => api.get('/users/roles'),
  getStatuses: () => api.get('/users/statuses'),
  getSellers: () => api.get('/users/sellers'),
  getTechnicians: () => api.get('/users/technicians'),
};

export const rbacAPI = {
  listPermissions: () => api.get('/rbac/permissions'),
  getPermissionMatrix: () => api.get('/rbac/permission-matrix'),
  getCoverage: () => api.get('/rbac/coverage'),
  listBaseRoles: () => api.get('/rbac/base-roles'),
  getBaseRole: (key) => api.get(`/rbac/base-roles/${key}`),
  updateBaseRole: (key, data) => api.put(`/rbac/base-roles/${key}`, data),
  deleteBaseRole: (key) => api.delete(`/rbac/base-roles/${key}`),
  listRoles: (includeDeleted = false) => api.get('/rbac/roles', { params: { include_deleted: includeDeleted } }),
  getRole: (id) => api.get(`/rbac/roles/${id}`),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.put(`/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  restoreRole: (id) => api.post(`/rbac/roles/${id}/restore`),
  listUserAssignments: () => api.get('/rbac/user-assignments'),
  assignRoleToUser: (userId, roleId, justification = '') => api.post(`/rbac/users/${userId}/assign-role`, { role_id: roleId, justification }),
  clearRoleFromUser: (userId, justification = '') => api.post(`/rbac/users/${userId}/assign-role`, { role_id: null, justification }),
  bulkAssignRole: (data) => api.post('/rbac/users/bulk-assign-role', data),
};

export const salesAPI = {
  list: (params) => api.get('/sales/', { params }),
  listCancelled: () => api.get('/sales/cancelled'),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales/', data),
  getOverrideAuthorizers: () => api.get('/sales/override-authorizers'),
  // Edit with mandatory justification (only Draft/Confirmed)
  editSale: (id, data, justification) => api.put(`/sales/${id}/edit`, { ...data, justification }),
  // State transitions
  confirmSale: (id) => api.put(`/sales/${id}/confirm`),
  markPaid: (id) => api.put(`/sales/${id}/pay`),
  completeSale: (id) => api.put(`/sales/${id}/complete`),
  // Cancel with mandatory reason
  cancelSale: (id, cancelReason, cancelNotes = '', revertStock = true) => api.post(`/sales/${id}/cancel`, {
    cancel_reason: cancelReason,
    cancel_notes: cancelNotes,
    revert_stock: revertStock,
  }),
  bulkStatus: (data) => api.post('/sales/bulk-status', data),
  purgeCancelled: (data) => api.post('/sales/admin/purge-cancelled', data),
  // Audit log
  getSaleAudit: (id) => api.get(`/sales/${id}/audit`),
  getAllAuditLogs: (limit = 100) => api.get('/sales/audit/all', { params: { limit } }),
  deleteAuditLog: (logId) => api.delete(`/sales/audit/${logId}`),
  // Statistics
  getSummary: (params) => api.get('/sales/statistics/summary', { params }),
  // Migration
  migrateToLocations: () => api.post('/sales/migrate-to-locations'),
};

// =============================================================================
// PRINT API - POS Térmica + PDFs A4
// =============================================================================
export const printAPI = {
  // POS thermal receipts - Use API_BASE_URL (sem /api duplicado)
  getSalePosUrl: (saleId, copyType = 'CLIENTE') => `${API_BASE_URL}/api/print/sales/${saleId}/pos?copy_type=${copyType}`,
  getSalePosDualUrl: (saleId) => `${API_BASE_URL}/api/print/sales/${saleId}/pos-dual`,
  
  // A4 documents
  getSaleInvoiceUrl: (saleId, copyType = 'Original') => `${API_BASE_URL}/api/print/sales/${saleId}/invoice.pdf?copy_type=${copyType}`,
  getSaleDeliveryNoteUrl: (saleId, copyType = 'Original') => `${API_BASE_URL}/api/print/sales/${saleId}/delivery-note.pdf?copy_type=${copyType}`,
  
  // Quotation PDF
  getQuotationPdfUrl: (quotationId, copyType = 'Original') => `${API_BASE_URL}/api/documents/quotations/${quotationId}/pdf?copy_type=${copyType}`,
  
  // Service documents - P0-CORE
  getServiceReceptionNoteUrl: (serviceId, copyType = 'Original') => `${API_BASE_URL}/api/print/service/${serviceId}/reception-note.pdf?copy_type=${copyType}`,
  getServiceFinalDeliveryUrl: (serviceId, copyType = 'Original') => `${API_BASE_URL}/api/print/service/${serviceId}/final-delivery.pdf?copy_type=${copyType}`,
  getServicePaymentReceiptUrl: (serviceId, paymentId = null, copyType = 'Original') => {
    let url = `${API_BASE_URL}/api/print/service/${serviceId}/payment-receipt.pdf?copy_type=${copyType}`;
    if (paymentId) url += `&payment_id=${paymentId}`;
    return url;
  },
  getServiceQuotationUrl: (serviceId, copyType = 'Original') => `${API_BASE_URL}/api/print/service/${serviceId}/quotation.pdf?copy_type=${copyType}`,
  
  /**
   * P0-2 FIX: Download PDF autenticado via fetch com Authorization header
   * 
   * @param {string} serviceId - ID do serviço
   * @param {string} docType - Tipo: 'reception', 'delivery', 'payment', 'quotation'
   * @param {string} copyType - 'Original' ou 'Cópia'
   * @param {string} paymentId - ID do pagamento (opcional, só para payment)
   * @param {string} action - 'preview' (abre nova aba) ou 'download' (força download)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  downloadServicePdf: async (serviceId, docType, copyType = 'Original', paymentId = null, action = 'preview') => {
    const token = getStoredAuthToken();
    if (!token) {
      return { success: false, error: 'Não autenticado' };
    }

    // Mapear tipo para endpoint
    const endpoints = {
      reception: 'reception-note.pdf',
      delivery: 'final-delivery.pdf',
      payment: 'payment-receipt.pdf',
      quotation: 'quotation.pdf'
    };

    const endpoint = endpoints[docType];
    if (!endpoint) {
      return { success: false, error: 'Tipo de documento inválido' };
    }

    let url = `${API_BASE_URL}/api/print/service/${serviceId}/${endpoint}?copy_type=${encodeURIComponent(copyType)}`;
    if (docType === 'payment' && paymentId) {
      url += `&payment_id=${paymentId}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Tentar ler erro JSON do body
        let errorMsg = `Erro ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          // Body não é JSON, usar status
        }
        return { success: false, error: errorMsg };
      }

      // Obter blob do PDF
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      if (action === 'download') {
        // Forçar download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${docType}_${serviceId}_${copyType}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        // Preview em nova aba
        window.open(blobUrl, '_blank');
        // Nota: não revoke imediatamente para permitir visualização
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }

      return { success: true };
    } catch (error) {
      console.error('PDF download error:', error);
      return { success: false, error: error.message || 'Erro de rede' };
    }
  },
  
  // Guide documents
  getReceptionGuidePdfUrl: (guideId, copyType = 'Original') => `${API_BASE_URL}/api/documents/reception-guides/${guideId}/pdf?copy_type=${copyType}`,
  getDeliveryGuidePdfUrl: (guideId, copyType = 'Original') => `${API_BASE_URL}/api/documents/delivery-guides/${guideId}/pdf?copy_type=${copyType}`,

  /**
   * Abre um blob PDF em nova aba ou força download
   */
  openPdfBlob: (blob, filename, action = 'preview') => {
    const blobUrl = URL.createObjectURL(blob);
    if (action === 'download') {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } else {
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    }
  },

  /**
   * P0-PRINT-01: Render PDF via Template Builder (autenticado via Bearer token)
   * 
   * Tenta renderizar via template freeform. Se não existir template configurado,
   * faz fallback para o sistema antigo (com aviso).
   * 
   * @param {Object} options - { doc_type, sale_id?, service_id?, quotation_id?, po_id?, transfer_id?, location_id?, receipt_id?, credit_note_id?, payment_id?, copy_type?, template_id? }
   * @param {string|Object} actionOrConfig - 'preview' | 'download' ou config
   * @param {Object} config - { deferOnMissingData?: boolean }
   * @returns {Promise<{success, fromTemplate, usedFallback, warningMessage?, error?, warnings?, hasMissingData?, blob?, filename?}>}
   */
  renderDocumentPdf: async (options, actionOrConfig = 'preview', config = {}) => {
    let action = actionOrConfig;
    let cfg = config;
    if (typeof actionOrConfig === 'object' && actionOrConfig !== null) {
      cfg = actionOrConfig;
      action = 'preview';
    }
    const token = getStoredAuthToken();
    if (!token) return { success: false, error: 'Não autenticado' };

    const {
      doc_type, sale_id, service_id, quotation_id, po_id, transfer_id, location_id, receipt_id, credit_note_id, payment_id,
      copy_type = 'Original', template_id = null, module_group = null
    } = options;

    const { deferOnMissingData = false } = cfg || {};

    try {
      const tryPosFallback = async (fallbackUrl, warningMsg) => {
        if (doc_type !== 'pos_receipt' || !fallbackUrl) return null;

        const fbResp = await fetch(`${API_BASE_URL}${fallbackUrl}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!fbResp.ok) {
          return { success: false, error: `Fallback POS falhou (${fbResp.status})` };
        }

        const html = await fbResp.text();
        const fbWin = window.open('', '_blank');
        if (fbWin) {
          fbWin.document.write(html);
          fbWin.document.close();
        }

        return {
          success: true,
          fromTemplate: false,
          usedFallback: true,
          warningMessage: warningMsg || 'Template POS indisponível. A usar modelo antigo.'
        };
      };

      // 1. Tentar template freeform
      const resp = await fetch(`${API_BASE_URL}/api/templates/render-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doc_type,
          sale_id,
          service_id,
          quotation_id,
          po_id,
          transfer_id,
          location_id,
          receipt_id,
          credit_note_id,
          payment_id,
          copy_type,
          template_id,
          module_group
        })
      });

      if (resp.ok) {
        // Ler warnings de campos em falta (não bloqueia a impressão)
        const validationWarnings = resp.headers.get('X-Validation-Warnings');
        const missingData = resp.headers.get('X-Missing-Master-Data') === 'true';
        let warnings = [];
        if (validationWarnings) {
          try { warnings = JSON.parse(validationWarnings); } catch (error) { void error; }
        }

        const resolvedTemplateId = resp.headers.get('X-Resolved-Template-Id');
        const resolvedTemplateName = resp.headers.get('X-Resolved-Template-Name');
        const resolvedTemplateRule = resp.headers.get('X-Resolved-Template-Rule');
        const resolvedDocType = resp.headers.get('X-Resolved-Doc-Type');
        const resolvedModuleGroup = resp.headers.get('X-Resolved-Module-Group');
        const runtimeLocationId = resp.headers.get('X-Runtime-Location-Id');

        const docRef = sale_id || service_id || quotation_id || po_id || transfer_id || receipt_id || credit_note_id || payment_id || 'doc';
        const filename = `${doc_type}_${docRef}_${copy_type}.pdf`;
        const blob = await resp.blob();

        if (missingData && deferOnMissingData) {
          return {
            success: true,
            fromTemplate: true,
            warnings,
            hasMissingData: missingData,
            blob,
            filename,
            resolvedTemplateId,
            resolvedTemplateName,
            resolvedTemplateRule,
            resolvedDocType,
            resolvedModuleGroup,
            runtimeLocationId,
          };
        }

        printAPI.openPdfBlob(blob, filename, action);
        return {
          success: true,
          fromTemplate: true,
          warnings,
          hasMissingData: missingData,
          resolvedTemplateId,
          resolvedTemplateName,
          resolvedTemplateRule,
          resolvedDocType,
          resolvedModuleGroup,
          runtimeLocationId,
        };
      }

      // 2. Fallback: template não configurado (404)
      if (resp.status === 404) {
        let errorData = null;
        try { errorData = await resp.json(); } catch (error) { void error; }

        const fallbackUrl = errorData?.detail?.fallback_url;
        const warningMsg = errorData?.detail?.message || 'Template não configurado. A usar modelo antigo.';

        if (fallbackUrl) {
          if (doc_type === 'pos_receipt') {
            const posFallbackResult = await tryPosFallback(fallbackUrl, warningMsg);
            if (posFallbackResult) return posFallbackResult;
          } else {
            // PDF fallback
            const fbResp = await fetch(`${API_BASE_URL}${fallbackUrl}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (fbResp.ok) {
              const fbBlob = await fbResp.blob();
              printAPI.openPdfBlob(fbBlob, `${doc_type}_fallback_${copy_type}.pdf`, action);
              return { success: true, fromTemplate: false, usedFallback: true, warningMessage: warningMsg };
            } else {
              return { success: false, error: `Fallback falhou (${fbResp.status})` };
            }
          }
        }
        return { success: false, error: warningMsg };
      }

      if (doc_type === 'pos_receipt' && sale_id) {
        const posFallbackResult = await tryPosFallback(
          `/api/print/sales/${sale_id}/pos-dual`,
          'Template POS falhou. A usar modelo antigo.'
        );
        if (posFallbackResult?.success) {
          return posFallbackResult;
        }
      }

      // Outro erro
      let errMsg = `Erro ${resp.status}`;
      try {
        const ed = await resp.json();
        errMsg = ed?.detail?.message || ed?.detail || errMsg;
      } catch (error) { void error; }
      return { success: false, error: errMsg };

    } catch (err) {
      return { success: false, error: err.message || 'Erro de rede' };
    }
  },
  
  // Settings - these use the axios instance which has /api prefix
  getSettings: (locationId = null) => api.get('/print/settings', { params: { location_id: locationId } }),
  listAllSettings: () => api.get('/print/settings/all'),
  saveSettings: (data) => api.post('/print/settings', data),
  deleteSettings: (id) => api.delete(`/print/settings/${id}`),
};

// Settings API - All Configurable Entities
export const settingsAPI = {
  // Locations (Localizações - Lojas, Armazéns, etc.)
  listLocations: (includeDeleted = false) => getCachedApiGet(
    `settings.locations.${includeDeleted ? 'deleted' : 'active'}`,
    () => api.get('/settings/locations', { params: { include_deleted: includeDeleted } }),
    10000,
  ),
  listActiveLocations: () => getCachedApiGet(
    'settings.active-locations',
    () => api.get('/settings/locations/active'),
    60000,
  ),
  listLocationsByType: (type) => api.get(`/settings/locations/by-type/${type}`),
  listDeletedLocations: () => api.get('/settings/locations/deleted'),
  getLocation: (id) => api.get(`/settings/locations/${id}`),
  getLocationItems: (id) => api.get(`/settings/locations/${id}/items`),
  getLocationStockSummary: (id) => api.get(`/settings/locations/${id}/stock-summary`),
  createLocation: (data) => api.post('/settings/locations', data),
  updateLocation: (id, data, justification) => api.put(`/settings/locations/${id}`, { ...data, justification }),
  deleteLocation: (id, deleteReason) => api.delete(`/settings/locations/${id}`, { data: { delete_reason: deleteReason } }),
  restoreLocation: (id) => api.post(`/settings/locations/${id}/restore`),
  
  // Payment Methods (Formas de Pagamento)
  listPaymentMethods: (includeDeleted = false) => api.get('/settings/payment-methods', { params: { include_deleted: includeDeleted } }),
  listActivePaymentMethods: () => api.get('/settings/payment-methods/active'),
  listDeletedPaymentMethods: () => api.get('/settings/payment-methods/deleted'),
  createPaymentMethod: (data) => api.post('/settings/payment-methods', data),
  updatePaymentMethod: (id, data, justification) => api.put(`/settings/payment-methods/${id}`, { ...data, justification }),
  deletePaymentMethod: (id, deleteReason) => api.delete(`/settings/payment-methods/${id}`, { data: { delete_reason: deleteReason } }),
  restorePaymentMethod: (id) => api.post(`/settings/payment-methods/${id}/restore`),
  
  // Payment Accounts (Contas de Pagamento)
  listPaymentAccounts: (includeDeleted = false) => api.get('/settings/payment-accounts', { params: { include_deleted: includeDeleted } }),
  listActivePaymentAccounts: () => api.get('/settings/payment-accounts/active'),
  listDeletedPaymentAccounts: () => api.get('/settings/payment-accounts/deleted'),
  getPaymentAccountSalesHistory: (id, limit = 50) => api.get(`/settings/payment-accounts/${id}/sales-history`, { params: { limit } }),
  createPaymentAccount: (data) => api.post('/settings/payment-accounts', data),
  updatePaymentAccount: (id, data, justification) => api.put(`/settings/payment-accounts/${id}`, { ...data, justification }),
  deletePaymentAccount: (id, deleteReason) => api.delete(`/settings/payment-accounts/${id}`, { data: { delete_reason: deleteReason } }),
  restorePaymentAccount: (id) => api.post(`/settings/payment-accounts/${id}/restore`),
  
  // Employees (Funcionários)
  listEmployees: (includeDeleted = false) => api.get('/settings/employees', { params: { include_deleted: includeDeleted } }),
  listActiveEmployees: (options = {}) => {
    const params = { ...(options || {}) };
    const compactSuffix = params.compact ? 'compact' : 'full';
    return getCachedApiGet(
      `settings.active-employees.${compactSuffix}`,
      () => api.get('/settings/employees/active', { params }),
      params.compact ? 60000 : 15000,
    );
  },
  listDeletedEmployees: () => api.get('/settings/employees/deleted'),
  createEmployee: (data) => api.post('/settings/employees', data),
  updateEmployee: (id, data, justification) => api.put(`/settings/employees/${id}`, { ...data, justification }),
  deleteEmployee: (id, deleteReason) => api.delete(`/settings/employees/${id}`, { data: { delete_reason: deleteReason } }),
  restoreEmployee: (id) => api.post(`/settings/employees/${id}/restore`),
  bulkEmployeeStatus: (data) => api.post('/settings/employees/bulk-status', data),
  exportEmployeesXlsx: (params) => api.get('/settings/employees/export/xlsx', { params, responseType: 'blob' }),
  
  // Payment Conditions (Condições de Pagamento)
  listPaymentConditions: (includeDeleted = false) => api.get('/settings/payment-conditions', { params: { include_deleted: includeDeleted } }),
  listActivePaymentConditions: () => api.get('/settings/payment-conditions/active'),
  listDeletedPaymentConditions: () => api.get('/settings/payment-conditions/deleted'),
  createPaymentCondition: (data) => api.post('/settings/payment-conditions', data),
  updatePaymentCondition: (id, data, justification) => api.put(`/settings/payment-conditions/${id}`, { ...data, justification }),
  deletePaymentCondition: (id, deleteReason) => api.delete(`/settings/payment-conditions/${id}`, { data: { delete_reason: deleteReason } }),
  restorePaymentCondition: (id) => api.post(`/settings/payment-conditions/${id}/restore`),
  
  // Due Date Options (Vencimento do Documento)
  listDueDateOptions: (includeDeleted = false) => api.get('/settings/due-date-options', { params: { include_deleted: includeDeleted } }),
  listActiveDueDateOptions: () => api.get('/settings/due-date-options/active'),
  listDeletedDueDateOptions: () => api.get('/settings/due-date-options/deleted'),
  createDueDateOption: (data) => api.post('/settings/due-date-options', data),
  updateDueDateOption: (id, data, justification) => api.put(`/settings/due-date-options/${id}`, { ...data, justification }),
  deleteDueDateOption: (id, deleteReason) => api.delete(`/settings/due-date-options/${id}`, { data: { delete_reason: deleteReason } }),
  restoreDueDateOption: (id) => api.post(`/settings/due-date-options/${id}/restore`),
  
  // Warranty Options (Tempo de Garantia)
  listWarrantyOptions: (includeDeleted = false) => api.get('/settings/warranty-options', { params: { include_deleted: includeDeleted } }),
  listActiveWarrantyOptions: () => api.get('/settings/warranty-options/active'),
  listDeletedWarrantyOptions: () => api.get('/settings/warranty-options/deleted'),
  createWarrantyOption: (data) => api.post('/settings/warranty-options', data),
  updateWarrantyOption: (id, data, justification) => api.put(`/settings/warranty-options/${id}`, { ...data, justification }),
  deleteWarrantyOption: (id, deleteReason) => api.delete(`/settings/warranty-options/${id}`, { data: { delete_reason: deleteReason } }),
  restoreWarrantyOption: (id) => api.post(`/settings/warranty-options/${id}/restore`),

  // Document prefixes
  getDocumentPrefixes: () => api.get('/settings/document-prefixes'),
  getDocumentPrefixDiagnostics: () => api.get('/settings/document-prefixes/diagnostics'),
  updateDocumentPrefixes: (data) => api.put('/settings/document-prefixes', data),
  
  // Seed initial data
  seedInitialData: () => api.post('/settings/seed-initial-data'),
  
  // Migrate items to locations
  migrateItemsToLocations: () => api.post('/settings/migrate-items-to-locations'),
  
  // Audit logs
  getAuditLogs: (entityType = null, limit = 100) => api.get('/settings/audit-logs', { params: { entity_type: entityType, limit } }),
};

export const dataMigrationAPI = {
  getSummary: () => getCachedApiGet('data-migration.summary', () => api.get('/data-migration/summary'), 30000),
  getHistory: (limit = 20) => api.get('/data-migration/history', { params: { limit } }),
  downloadHistoryReport: (entryId) => api.get(`/data-migration/history/${entryId}/report`, { responseType: 'blob' }),
  downloadExport: (scope = 'primary') => {
    const formData = new FormData();
    formData.append('scope', scope);
    return api.post('/data-migration/export/archive', formData, { responseType: 'blob' });
  },
  dryRunImport: (formData) => api.post('/data-migration/import/dry-run', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  runImport: (formData) => api.post('/data-migration/import/run', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// =============================================================================
// VAT/IVA API - Gestão de Imposto sobre Valor Acrescentado (Moçambique 16%)
// =============================================================================
export const vatAPI = {
  // Get current VAT configuration
  getConfig: () => getCachedApiGet('vat.config', () => api.get('/vat/config'), 30000),
  
  // Update VAT configuration (Admin only)
  updateConfig: (data) => api.put('/vat/config', data),
  
  // Calculate VAT for a price
  calculate: (price, vatRate = 16.0, regime = 'inclusive') => 
    api.get('/vat/calculate', { params: { price, vat_rate: vatRate, regime } }),
  
  // List available VAT regimes
  listRegimes: () => api.get('/vat/regimes'),
};

// Catalogs API (Phase A - Product Categories, Subcategories, Services, Expenses, etc.)
export const catalogsAPI = {
  // Product Categories & Subcategories
  listProductCategories: (includeInactive = false, includeDeleted = false) => 
    api.get('/catalogs/product-categories', { params: { include_inactive: includeInactive, include_deleted: includeDeleted } }),
  getProductCategory: (id) => api.get(`/catalogs/product-categories/${id}`),
  createProductCategory: (data) => api.post('/catalogs/product-categories', data),
  updateProductCategory: (id, data) => api.put(`/catalogs/product-categories/${id}`, data),
  checkCategoryUsage: (id) => api.get(`/catalogs/product-categories/${id}/usage`),
  deleteProductCategory: (id, force = false) => api.delete(`/catalogs/product-categories/${id}`, { params: { force } }),
  restoreProductCategory: (id) => api.post(`/catalogs/product-categories/${id}/restore`),
  
  listProductSubcategories: (categoryId = null, includeInactive = false, includeDeleted = false) => 
    api.get('/catalogs/product-subcategories', { params: { category_id: categoryId, include_inactive: includeInactive, include_deleted: includeDeleted } }),
  createProductSubcategory: (data) => api.post('/catalogs/product-subcategories', data),
  updateProductSubcategory: (id, data) => api.put(`/catalogs/product-subcategories/${id}`, data),
  checkSubcategoryUsage: (id) => api.get(`/catalogs/product-subcategories/${id}/usage`),
  deleteProductSubcategory: (id, force = false) => api.delete(`/catalogs/product-subcategories/${id}`, { params: { force } }),
  restoreProductSubcategory: (id) => api.post(`/catalogs/product-subcategories/${id}/restore`),
  getNextProductCode: (subcategoryId) => api.post(`/catalogs/product-subcategories/${subcategoryId}/next-code`),
  reserveProductCode: (subcategoryId) => api.post(`/catalogs/product-subcategories/${subcategoryId}/reserve-code`),
  
  // Service Catalog (SP/GP)
  listServiceCatalog: (serviceGroup = null, includeInactive = false) => 
    api.get('/catalogs/service-catalog', { params: { service_group: serviceGroup, include_inactive: includeInactive } }),
  listServiceGroups: () => api.get('/catalogs/service-catalog/groups'),
  createServiceCatalogItem: (data) => api.post('/catalogs/service-catalog', data),
  updateServiceCatalogItem: (id, data) => api.put(`/catalogs/service-catalog/${id}`, data),
  deleteServiceCatalogItem: (id) => api.delete(`/catalogs/service-catalog/${id}`),
  
  // Expense Categories (PGT)
  listExpenseCategories: (includeInactive = false, includeSubcategories = true, includeDeleted = false) =>
    getCachedApiGet(
      `catalogs.expense-categories:${includeInactive}:${includeSubcategories}:${includeDeleted}`,
      () => api.get('/catalogs/expense-categories', { params: { include_inactive: includeInactive, include_subcategories: includeSubcategories, include_deleted: includeDeleted } }),
      30000,
    ),
  createExpenseCategory: (data) => api.post('/catalogs/expense-categories', data),
  updateExpenseCategory: (id, data) => api.put(`/catalogs/expense-categories/${id}`, data),
  getExpenseCategoryUsage: (id) => api.get(`/catalogs/expense-categories/${id}/usage`),
  deleteExpenseCategory: (id, force = false) => api.delete(`/catalogs/expense-categories/${id}`, { params: { force } }),
  restoreExpenseCategory: (id) => api.post(`/catalogs/expense-categories/${id}/restore`),
  
  // Repair Service Types (SR)
  listRepairServiceTypes: (category = null, includeInactive = false) => 
    api.get('/catalogs/repair-service-types', { params: { category: category, include_inactive: includeInactive } }),
  listRepairCategories: () => api.get('/catalogs/repair-service-types/categories'),
  createRepairServiceType: (data) => api.post('/catalogs/repair-service-types', data),
  updateRepairServiceType: (id, data) => api.put(`/catalogs/repair-service-types/${id}`, data),
  
  // Cash Operation Types (TRF)
  listCashOperationTypes: (direction = null, includeInactive = false) => 
    api.get('/catalogs/cash-operation-types', { params: { direction: direction, include_inactive: includeInactive } }),
  createCashOperationType: (data) => api.post('/catalogs/cash-operation-types', data),
  
  // Cash Box Types (CX) - Diretiva Executiva
  listCashBoxTypes: (includeInactive = false, includeDeleted = false) => 
    api.get('/catalogs/cash-box-types', { params: { include_inactive: includeInactive, include_deleted: includeDeleted } }),
  getCashBoxType: (id) => api.get(`/catalogs/cash-box-types/${id}`),
  createCashBoxType: (data) => api.post('/catalogs/cash-box-types', data),
  updateCashBoxType: (id, data) => api.put(`/catalogs/cash-box-types/${id}`, data),
  deleteCashBoxType: (id, reason) => api.delete(`/catalogs/cash-box-types/${id}`, { params: { reason } }),
  restoreCashBoxType: (id) => api.post(`/catalogs/cash-box-types/${id}/restore`),
  getCashBoxTypeUsage: (id) => api.get(`/catalogs/cash-box-types/${id}/usage`),
  
  // Seed all catalogs
  seedCashOperationTypes: () => api.post('/catalogs/seed/cash-operation-types'),
  seedAll: () => api.post('/catalogs/seed/all'),
};

// Expenses API (Fase B - Módulo de Despesas)
export const expensesAPI = {
  // CRUD
  list: (params = {}) => api.get('/expenses/', { params }),
  listSubcategories: (categoryId, includeInactive = false) => api.get(`/expenses/subcategories/${categoryId}`, { params: { include_inactive: includeInactive } }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses/', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id, reason) => api.delete(`/expenses/${id}`, { params: { reason } }),
  permanentDelete: (id, reason) => api.delete(`/expenses/${id}/permanent`, { params: { reason } }),
  receiveInventory: (id, data = {}) => api.post(`/expenses/${id}/inventory/receive`, data),
  cancelInventory: (id, reason) => api.post(`/expenses/${id}/inventory/cancel`, null, { params: { reason } }),
  
  // Workflow
  submit: (id) => api.post(`/expenses/${id}/submit`),
  approve: (id, notes = null) => api.post(`/expenses/${id}/approve`, { action: 'approve', notes }),
  reject: (id, reason) => api.post(`/expenses/${id}/approve`, { action: 'reject', notes: reason }),
  markPaid: (id, data = {}) => api.post(`/expenses/${id}/pay`, data),
  renumberCodes: (data = {}) => api.post('/expenses/admin/renumber-codes', data),
  
  // Audit
  getAuditLog: (id) => api.get(`/expenses/${id}/audit`),
  
  // Reports
  getSummary: (params = {}) => api.get('/expenses/reports/summary', { params }),
  getPgtAnalytics: (params = {}) => api.get('/expenses/reports/pgt-analytics', { params }),
  getPgt01OperationalReport: (params = {}) => api.get('/expenses/reports/pgt01-operational', { params }),
  getMonthlyReport: (year, locationId = null) => 
    api.get('/expenses/reports/monthly', { params: { year, location_id: locationId } }),
};

// Cash Management API (Fase C - Gestão de Caixa)
export const cashAPI = {
  // Sessions
  openSession: (data) => api.post('/cash/sessions/open', data),
  closeSession: (sessionId, data) => api.post(`/cash/sessions/${sessionId}/close`, data),
  getActiveSession: (locationId) => api.get(`/cash/sessions/active/${locationId}`),
  listSessions: (params = {}) => api.get('/cash/sessions/', { params }),
  getSession: (id) => api.get(`/cash/sessions/${id}`),
  
  // Transfers (TRF)
  createTransfer: (data) => api.post('/cash/transfers/', data),
  listTransfers: (params = {}) => api.get('/cash/transfers/', { params }),
  getTransfer: (id) => api.get(`/cash/transfers/${id}`),
  
  // Checkpoints
  registerCheckpoint: (locationId, balance, notes = null, checkpoint_time = null) => 
    api.post('/cash/checkpoints/register', null, { params: { location_id: locationId, balance, notes, checkpoint_time } }),
  listCheckpoints: (params = {}) => api.get('/cash/checkpoints/', { params }),
  
  // Alerts
  listAlerts: (params = {}) => api.get('/cash/alerts/', { params }),
  getAlertClassificationSummary: () => api.get('/cash/alerts/classification-summary'),
  resolveAlert: (id, resolution_notes) => api.post(`/cash/alerts/${id}/resolve`, null, { params: { resolution_notes } }),
  
  // Scheduler
  getSchedulerStatus: () => api.get('/cash/scheduler/status'),
  triggerManualCheck: (checkType = 'checkpoint') => api.post('/cash/scheduler/trigger', null, { params: { check_type: checkType } }),
  
  // Reports
  getAuditLogs: (params = {}) => api.get('/cash/audit/', { params }),
  getDailySummary: (locationId, date = null) => 
    api.get('/cash/summary/daily', { params: { location_id: locationId, date } }),
};

export const servicesAPI = {
  list: (params) => api.get('/services/', { params }),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services/', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  // New: Edit with mandatory justification
  editWithAudit: (id, data, justification) => api.put(`/services/${id}/edit`, { ...data, justification }),
  // New: Add admin note to closed services
  addAdminNote: (id, note, justification) => api.post(`/services/${id}/admin-note`, { note, justification }),
  // Soft delete with mandatory reason
  delete: (id, deleteReason) => api.delete(`/services/${id}`, { data: { delete_reason: deleteReason } }),
  restore: (id) => api.post(`/services/${id}/restore`),
  bulkStatus: (data) => api.post('/services/bulk-status', data),
  getSummary: () => api.get('/services/statistics/summary'),
  getDiagnosticCosts: () => api.get('/services/diagnostic-costs'),
  completeReception: (id, checklist) => api.post(`/services/${id}/reception`, checklist),
  updateReception: (id, checklist, correctionReason) => api.put(`/services/${id}/reception`, { checklist, correction_reason: correctionReason }),
  start: (id) => api.post(`/services/${id}/start`),
  complete: (id, notes) => api.post(`/services/${id}/complete`, null, { params: { final_notes: notes } }),
  registerBatteryCheck: (id, percentage, notes) => 
    api.post(`/services/${id}/battery-check`, null, { params: { percentage, notes } }),
  correctBatteryCheck: (serviceId, checkId, percentage, reason) =>
    api.put(`/services/${serviceId}/battery-check/${checkId}`, null, { params: { new_percentage: percentage, correction_reason: reason } }),
  getPendingBatteryChecks: () => api.get('/services/battery-checks/pending'),
  getAuditLog: (id) => api.get(`/services/${id}/audit-log`),
  getRecentAuditLogs: (limit) => api.get('/services/audit-logs/recent', { params: { limit } }),
  
  // ========== REPAIR WORKFLOW APIs ==========
  // Diagnostic payment
  payDiagnostic: (id, paymentMethodId, reference, notes, paymentAccountId = null) => 
    api.post(`/services/${id}/diagnostic-payment`, {
      payment_method_id: paymentAccountId || paymentMethodId,
      payment_account_id: paymentAccountId,
      payment_reference: reference,
      notes
    }),
  
  // Parts management
  getAvailableParts: (search, locationId = null, inStockOnly = true) => api.get('/services/inventory/parts', {
    params: {
      search,
      location_id: locationId || undefined,
      in_stock_only: inStockOnly || undefined,
    }
  }),
  addPart: (id, productId, quantity) => api.post(`/services/${id}/parts`, { product_id: productId, quantity }),
  removePart: (id, productId) => api.delete(`/services/${id}/parts/${productId}`),
  
  // Labour cost
  updateLabour: (id, labourCost, description) => 
    api.put(`/services/${id}/labour`, null, { params: { labour_cost: labourCost, description } }),

  updateQuotationVAT: (id, vatEnabled, vatRate = 16) =>
    api.put(`/services/${id}/quotation-vat`, { vat_enabled: vatEnabled, vat_rate: vatRate }),
  
  // Quotation workflow
  sendQuotation: (id) => api.post(`/services/${id}/send-quotation`),
  processApproval: (id, approved, rejectionReason) => 
    api.post(`/services/${id}/customer-approval`, { approved, rejection_reason: rejectionReason }),
  
  // ========== P0-CORE: PAYMENTS LEDGER ==========
  // Lista todos os pagamentos de um serviço
  getPayments: (id) => api.get(`/services/${id}/payments`),
  
  // Pagamento único
  registerPayment: (id, amount, paymentMethodId, paymentType, reference, notes, paymentAccountId = null) =>
    api.post(`/services/${id}/payment`, {
      amount,
      payment_method_id: paymentAccountId || paymentMethodId,
      payment_account_id: paymentAccountId,
      payment_type: paymentType,
      reference,
      notes
    }),
  
  // Split payment (múltiplos métodos de pagamento) - P0-CORE
  registerSplitPayment: (id, payments, paymentType = 'final', notes = null) =>
    api.post(`/services/${id}/split-payment`, { payments, payment_type: paymentType, notes }),
  
  // ========== P0-CORE: STOCK & ASSETS ==========
  // Stock para reparação
  getRepairStock: (locationId, search) => 
    api.get('/services/inventory/repair-stock', { params: { location_id: locationId, search } }),
  
  // Ativos internos
  getInternalAssets: (locationId, search) => 
    api.get('/services/internal-assets', { params: { location_id: locationId, search } }),
  createInternalAsset: (data) => api.post('/services/internal-assets', data),
  
  // ========== P0-CORE: WARRANTY SEARCH ==========
  // Buscar vendas para garantia
  searchSalesForWarranty: (search, locationId = null) => api.get('/services/warranty/search-sales', {
    params: { search, location_id: locationId || undefined }
  }),
  createWarrantyClaim: (data) => api.post('/services/warranty/create-claim', data),

  // Service QA / Demo seed
  listQaScenarios: () => api.get('/services/qa/scenarios'),
  listQaRuns: (limit = 20) => api.get('/services/qa/runs', { params: { limit } }),
  getQaRun: (runId) => api.get(`/services/qa/runs/${runId}`),
  runQaScenario: (data) => api.post('/services/qa/runs', data),
  
  // Complete and invoice
  completeAndInvoice: (id, locationId = null) => api.post(`/services/${id}/complete-and-invoice`, null, {
    params: { location_id: locationId || undefined }
  }),
  getInvoicePdf: (id, locationId = null) => api.get(`/services/${id}/invoice/pdf`, {
    params: { location_id: locationId || undefined },
    responseType: 'blob'
  }),
  getServiceQuotationPdf: (id, copyType = 'Original', locationId = null) => api.get(`/print/service/${id}/quotation.pdf`, {
    params: { copy_type: copyType, location_id: locationId || undefined },
    responseType: 'blob'
  }),
  getServiceReceptionPdf: (id, copyType = 'Original', locationId = null) => api.get(`/print/service/${id}/reception-note.pdf`, {
    params: { copy_type: copyType, location_id: locationId || undefined },
    responseType: 'blob'
  }),
  getServiceDeliveryPdf: (id, copyType = 'Original', locationId = null) => api.get(`/print/service/${id}/final-delivery.pdf`, {
    params: { copy_type: copyType, location_id: locationId || undefined },
    responseType: 'blob'
  }),
  getServicePaymentReceiptPdf: (id, copyType = 'Original', locationId = null) => api.get(`/print/service/${id}/payment-receipt.pdf`, {
    params: { copy_type: copyType, location_id: locationId || undefined },
    responseType: 'blob'
  }),
};

export const procurementAPI = {
  // Suppliers
  listSuppliers: (includeDeleted = false) => api.get('/procurement/suppliers', { params: { include_deleted: includeDeleted } }),
  listDeletedSuppliers: () => api.get('/procurement/suppliers/deleted'),
  getSupplier: (id) => api.get(`/procurement/suppliers/${id}`),
  createSupplier: (data) => api.post('/procurement/suppliers', data),
  // Edit with mandatory justification
  editSupplier: (id, data, justification) => api.put(`/procurement/suppliers/${id}/edit`, { ...data, justification }),
  // Soft delete with mandatory reason
  deleteSupplier: (id, deleteReason) => api.delete(`/procurement/suppliers/${id}`, { data: { delete_reason: deleteReason } }),
  // Restore deleted supplier
  restoreSupplier: (id) => api.post(`/procurement/suppliers/${id}/restore`),
  // Audit log
  getSupplierAudit: (id) => api.get(`/procurement/suppliers/${id}/audit`),
  getAllSupplierAuditLogs: (limit = 100) => api.get('/procurement/audit/suppliers', { params: { limit } }),
  getSuppliersLedgerSummary: () => api.get('/procurement/suppliers-ledger/summary'),
  getSupplierLedgerDetail: (id) => api.get(`/procurement/suppliers/${id}/ledger`),
  exportSuppliersLedgerXlsx: (params = {}) => api.get('/procurement/suppliers-ledger/export/xlsx', { params, responseType: 'blob' }),
  
  // Purchase Orders
  listPOs: (params) => api.get('/procurement/purchase-orders', { params }),
  listCancelledPOs: () => api.get('/procurement/purchase-orders/cancelled'),
  getPO: (id) => api.get(`/procurement/purchase-orders/${id}`),
  createPO: (data) => api.post('/procurement/purchase-orders', data),
  // Edit with mandatory justification (only Draft POs)
  editPO: (id, data, justification) => api.put(`/procurement/purchase-orders/${id}/edit`, { ...data, justification }),
  approvePO: (id) => api.put(`/procurement/purchase-orders/${id}/approve`),
  receivePO: (id, data) => api.put(`/procurement/purchase-orders/${id}/receive`, data),
  closePO: (id) => api.put(`/procurement/purchase-orders/${id}/close`),
  registerPOPayment: (id, data) => api.put(`/procurement/purchase-orders/${id}/register-payment`, data),
  // Cancel with mandatory reason
  cancelPO: (id, cancelReason) => api.put(`/procurement/purchase-orders/${id}/cancel`, { cancel_reason: cancelReason }),
  bulkPOAction: (data) => api.post('/procurement/purchase-orders/bulk-action', data),
  exportPOsXlsx: (params) => api.get('/procurement/purchase-orders/export/xlsx', { params, responseType: 'blob' }),
  // Audit log
  getPOAudit: (id) => api.get(`/procurement/purchase-orders/${id}/audit`),
  getAllPOAuditLogs: (limit = 100) => api.get('/procurement/audit/purchase-orders', { params: { limit } }),
};

export const payrollAPI = {
  // Listagem e CRUD
  list: (params) => api.get('/payroll/', { params }),
  get: (id) => api.get(`/payroll/${id}`),
  update: (id, data) => api.put(`/payroll/${id}`, data),
  delete: (id) => api.delete(`/payroll/${id}`),
  adjustProcessedEmployee: (id, data) => api.post(`/payroll/${id}/adjust`, data),
  getAdjustmentHistory: (id) => api.get(`/payroll/${id}/adjustments`),
  
  // Processamento P0 Legal Moçambique
  process: (data) => api.post('/payroll/process', data),
  simulate: (data) => api.post('/payroll/simulate', data),
  getProcessingDefaults: (params) => api.get('/payroll/meta/processing-defaults', { params }),
  getHrAlerts: (params) => api.get('/payroll/meta/hr-alerts', { params }),
  getTodayBirthdays: () => getCachedApiGet('payroll.today-birthdays', () => api.get('/payroll/meta/today-birthdays'), 60000),
  confirm: (id) => api.put(`/payroll/${id}/confirm`),
  markPaid: (id) => api.put(`/payroll/${id}/pay`),
  closePeriod: (data) => api.post('/payroll/period-close', data),
  downloadDocumentsZip: (params) => api.get('/payroll/export/zip', { params, responseType: 'blob' }),
  
  // Recibo PDF
  getReceipt: (id) => `${API_URL}/payroll/${id}/receipt`,
  downloadReceipt: (id, version = null) => api.get(`/payroll/${id}/receipt`, { responseType: 'blob', params: version ? { version } : {} }),
  
  // Estatísticas
  getSummary: (params) => api.get('/payroll/statistics/summary', { params }),
  
  // Configurações INSS/IRPS
  getINSSConfig: () => api.get('/payroll/config/inss'),
  updateINSSConfig: (data) => api.put('/payroll/config/inss', null, { params: data }),
  deleteINSSConfig: () => api.delete('/payroll/config/inss'),
  getBirthdayBannerConfig: () => getCachedApiGet('payroll.birthday-banner-config', () => api.get('/payroll/config/birthday-banner'), 60000),
  updateBirthdayBannerConfig: (data) => api.put('/payroll/config/birthday-banner', data),
  getIRPSTables: () => api.get('/payroll/config/irps'),
  createIRPSTable: (data) => api.post('/payroll/config/irps', data),
  updateIRPSTable: (tableId, data) => api.put(`/payroll/config/irps/${tableId}`, data),
  activateIRPSTable: (tableId) => api.put(`/payroll/config/irps/${tableId}/activate`),
  deleteIRPSTable: (tableId) => api.delete(`/payroll/config/irps/${tableId}`),
  seedDefaultIRPS: () => api.post('/payroll/config/irps/seed-default'),
  applyOfficialIRPSYear: (year) => api.post(`/payroll/config/irps/official/${year}`),
  
  // Meta da empresa
  getCompanyGoal: () => api.get('/payroll/config/company-goal'),
  updateCompanyGoal: (data) => api.put('/payroll/config/company-goal', null, { params: data }),
  deleteCompanyGoal: () => api.delete('/payroll/config/company-goal'),
  
  // Metas de funcionário
  getEmployeeGoals: (employeeId) => api.get(`/payroll/employee/${employeeId}/goals`),
  updateEmployeeGoals: (employeeId, data) => api.put(`/payroll/employee/${employeeId}/goals`, data),
  
  // Dossier Digital (via users)
  getEmployeeDossier: (userId) => api.get(`/users/${userId}/dossier`, { params: { include_documents: false } }),
  updateEmployeeDossier: (userId, data) => api.put(`/users/${userId}/dossier`, data, { params: { include_documents: false } }),
  listEligibleEmployees: (params = {}) => api.get('/users/payroll/eligible', { params }),
  reintegrateEmployeeDossier: (userId, data) => api.post(`/users/${userId}/dossier/reintegrate`, data),
  listEmployeeDirectory: (params = {}) => api.get('/users/payroll/eligible', { params: { include_inactive: true, ...params } }),
  downloadAttendanceImportTemplateXlsx: (params = {}) => api.get('/payroll/attendance/import-template/xlsx', { params, responseType: 'blob' }),
  listAttendanceImportBatches: (params = {}) => api.get('/payroll/attendance/import-batches', { params }),
  importAttendanceCsv: (formData) => api.post('/payroll/attendance/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  createManualAttendanceEntry: (data) => api.post('/payroll/attendance/manual-entry', data),
  listAttendance: (params) => api.get('/payroll/attendance', { params }),
  listAttendanceShifts: () => api.get('/payroll/attendance-shifts'),
  saveAttendanceShift: (data) => api.post('/payroll/attendance-shifts', data),
  deleteAttendanceShift: (shiftId) => api.delete(`/payroll/attendance-shifts/${shiftId}`),
  updateAttendanceOvertimeAuthorization: (recordId, data) => api.post(`/payroll/attendance/${recordId}/overtime-authorization`, data),
  getAttendanceReport: (params) => api.get('/payroll/attendance/report', { params }),
  getAttendanceBootstrapStatus: () => api.get('/payroll/attendance/bootstrap-status'),
  resetAttendanceOperationalData: (data) => api.post('/payroll/attendance/reset-operational-data', data),
  resetAttendanceMonthData: (data) => api.post('/payroll/attendance/month-data/reset', data),
  exportAttendanceXlsx: (params) => `${API_BASE_URL}/api/payroll/attendance/export/xlsx?month=${params.month}&year=${params.year}`,
  downloadAttendanceXlsx: (params) => api.get('/payroll/attendance/export/xlsx', { params, responseType: 'blob' }),
  getAttendanceWeeklyMap: (params) => api.get('/payroll/attendance/weekly-map', { params }),
  getAttendancePendingIssues: (params = {}) => api.get('/payroll/attendance/pending-issues', { params }),
  getAttendanceValidationIssues: (params = {}) => api.get('/payroll/attendance/validation-issues', { params }),
  listAttendanceJustificationTypes: () => api.get('/payroll/attendance/justification-types'),
  listJustifiedAbsences: (params) => api.get('/payroll/attendance/justified-absences', { params }),
  createJustifiedAbsence: (data) => api.post('/payroll/attendance/justified-absences', data),
  updateJustifiedAbsence: (absenceId, data) => api.put(`/payroll/attendance/justified-absences/${absenceId}`, data),
  deleteJustifiedAbsence: (absenceId) => api.delete(`/payroll/attendance/justified-absences/${absenceId}`),
  listEmployeeStoreAllocations: (params = {}) => api.get('/payroll/employee-store-allocations', { params }),
  createEmployeeStoreAllocation: (data) => api.post('/payroll/employee-store-allocations', data),
  updateEmployeeStoreAllocation: (allocationId, data) => api.put(`/payroll/employee-store-allocations/${allocationId}`, data),
  deleteEmployeeStoreAllocation: (allocationId) => api.delete(`/payroll/employee-store-allocations/${allocationId}`),
  updateAttendanceRecord: (recordId, data) => api.put(`/payroll/attendance/${recordId}`, data),
  approveAttendanceSwap: (recordId, data) => api.post(`/payroll/attendance/${recordId}/swap-approval`, data),
  deleteAttendanceRecord: (recordId) => api.delete(`/payroll/attendance/${recordId}`),
  closeAttendanceMonth: (data) => api.post('/payroll/attendance/month-close', data),
  listAttendanceMonthClosures: (params = {}) => api.get('/payroll/attendance/month-close', { params }),
  reviewAttendanceMonthClose: (closureId, data) => api.post(`/payroll/attendance/month-close/${closureId}/review`, data),
  getPayrollAggregationPreview: (params) => api.get('/payroll/preview/aggregation', { params }),
  syncProfileRoleAlignment: () => api.post('/payroll/backfill/profile-role-alignment'),
  getSalarySheetPreview: (params) => api.get('/payroll/reports/salary-sheet', { params }),
  downloadSalarySheetPdf: (params) => api.get('/payroll/reports/salary-sheet/pdf', { params, responseType: 'blob' }),
  downloadSalarySheetXlsx: (params) => api.get('/payroll/reports/salary-sheet/xlsx', { params, responseType: 'blob' }),
  getNominalPreview: (params) => api.get('/payroll/reports/nominal', { params }),
  downloadNominalPdf: (params) => api.get('/payroll/reports/nominal/pdf', { params, responseType: 'blob' }),
  downloadNominalXlsx: (params) => api.get('/payroll/reports/nominal/xlsx', { params, responseType: 'blob' }),
  getAttendanceDocumentPreview: (params) => api.get('/payroll/reports/attendance-map', { params }),
  downloadAttendanceDocumentPdf: (params) => api.get('/payroll/reports/attendance-map/pdf', { params, responseType: 'blob' }),
  downloadAttendanceDocumentXlsx: (params) => api.get('/payroll/reports/attendance-map/xlsx', { params, responseType: 'blob' }),
  getVacationPolicy: () => api.get('/payroll/vacations/policy'),
  updateVacationPolicy: (data) => api.put('/payroll/vacations/policy', data),
  getVacationsDashboard: (year) => api.get('/payroll/vacations/dashboard', { params: { year } }),
  createVacationRequest: (data) => api.post('/payroll/vacations/requests', data),
  updateVacationRequestStatus: (requestId, data) => api.put(`/payroll/vacations/requests/${requestId}/status`, data),
  getVacationRequestsReport: (params) => api.get('/payroll/reports/vacations/requests', { params }),
  downloadVacationRequestsReportPdf: (params) => api.get('/payroll/reports/vacations/requests/pdf', { params, responseType: 'blob' }),
  downloadVacationRequestsReportXlsx: (params) => api.get('/payroll/reports/vacations/requests/xlsx', { params, responseType: 'blob' }),
  getVacationGeneralPlanReport: (params) => api.get('/payroll/reports/vacations/plan-general', { params }),
  downloadVacationGeneralPlanReportPdf: (params) => api.get('/payroll/reports/vacations/plan-general/pdf', { params, responseType: 'blob' }),
  downloadVacationGeneralPlanReportXlsx: (params) => api.get('/payroll/reports/vacations/plan-general/xlsx', { params, responseType: 'blob' }),
  getVacationIndividualPlanReport: (params) => api.get('/payroll/reports/vacations/plan-individual', { params }),
  downloadVacationIndividualPlanReportPdf: (params) => api.get('/payroll/reports/vacations/plan-individual/pdf', { params, responseType: 'blob' }),
  downloadVacationIndividualPlanReportXlsx: (params) => api.get('/payroll/reports/vacations/plan-individual/xlsx', { params, responseType: 'blob' }),
  getVacationBalanceSummaryReport: (params) => api.get('/payroll/reports/vacations/balance-summary', { params }),
  downloadVacationBalanceSummaryReportPdf: (params) => api.get('/payroll/reports/vacations/balance-summary/pdf', { params, responseType: 'blob' }),
  downloadVacationBalanceSummaryReportXlsx: (params) => api.get('/payroll/reports/vacations/balance-summary/xlsx', { params, responseType: 'blob' }),
  getTemplateSettings: () => api.get('/payroll/settings/templates'),
  saveTemplateSettings: (data) => api.put('/payroll/settings/templates', data),
  previewTemplatePdf: (data) => api.post('/payroll/settings/templates/preview-pdf', data, { responseType: 'blob' }),
  exportTemplateTestXlsx: (data) => api.post('/payroll/settings/templates/test-xlsx', data, { responseType: 'blob' }),
};

export const operationalExecutionAPI = {
  getBootstrap: (params = {}) => api.get('/operational-execution/bootstrap', { params }),
  listCatalog: () => api.get('/operational-execution/catalog'),
  createCatalogActivity: (data) => api.post('/operational-execution/catalog', data),
  updateCatalogActivity: (id, data) => api.put(`/operational-execution/catalog/${id}`, data),
  setCatalogActivityStatus: (id, data) => api.put(`/operational-execution/catalog/${id}/status`, data),
  deleteCatalogActivity: (id) => api.delete(`/operational-execution/catalog/${id}`),
  listReasons: () => api.get('/operational-execution/reasons'),
  createReason: (data) => api.post('/operational-execution/reasons', data),
  updateReason: (id, data) => api.put(`/operational-execution/reasons/${id}`, data),
  generateActivities: (data) => api.post('/operational-execution/generate', data),
  getMyActivities: (params = {}) => api.get('/operational-execution/activities/my', { params }),
  getOverview: (params = {}) => api.get('/operational-execution/activities/overview', { params }),
  completeActivity: (id, data) => api.post(`/operational-execution/activities/${id}/complete`, data),
  justifyActivity: (id, data) => api.post(`/operational-execution/activities/${id}/justify`, data),
  commentActivity: (id, data) => api.post(`/operational-execution/activities/${id}/comment`, data),
  requestCorrection: (id, data) => api.post(`/operational-execution/activities/${id}/request-correction`, data),
  adminAdjustActivity: (id, data) => api.post(`/operational-execution/activities/${id}/admin-adjust`, data),
  listCorrections: (params = {}) => api.get('/operational-execution/corrections', { params }),
  reviewCorrection: (id, data) => api.post(`/operational-execution/corrections/${id}/review`, data),
  getDashboard: (params = {}) => api.get('/operational-execution/dashboard', { params }),
};

export const whatsappAPI = {
  // Status e templates
  getStatus: () => api.get('/whatsapp/status'),
  getTemplates: () => api.get('/whatsapp/templates'),
  
  // Envio de mensagens
  sendTemplate: (data) => api.post('/whatsapp/send-template', data),
  sendCustom: (data) => api.post('/whatsapp/send-custom', data),
  sendTest: (phone) => api.post(`/whatsapp/test?phone=${encodeURIComponent(phone)}`),
  
  // Envio para serviços
  sendWarrantyResponse: (serviceId, templateType, extraVariables) => 
    api.post(`/whatsapp/send-warranty-response/${serviceId}?template_type=${templateType}`, extraVariables),
  sendRepairStatus: (serviceId, statusType) => 
    api.post(`/whatsapp/send-repair-status/${serviceId}?status_type=${statusType}`),
  
  // Histórico
  getHistory: (params) => api.get('/whatsapp/history', { params }),
  getMessageDetails: (messageId) => api.get(`/whatsapp/history/${messageId}`),
  
  // Sandbox management
  checkSandboxRegistration: (phone) => api.get(`/whatsapp/sandbox/check/${encodeURIComponent(phone)}`),
  registerSandboxNumber: (phone) => api.post(`/whatsapp/sandbox/register/${encodeURIComponent(phone)}`),
  unregisterSandboxNumber: (phone) => api.delete(`/whatsapp/sandbox/unregister/${encodeURIComponent(phone)}`),
  listRegisteredNumbers: () => api.get('/whatsapp/sandbox/registered'),
  getSandboxQrCode: () => `${API_BASE_URL}/whatsapp/sandbox/qrcode`,
};

export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getSalesBySeller: (params) => api.get('/reports/sales-by-seller', { params }),
  getInventoryValue: () => api.get('/reports/inventory-value'),
  getTopProducts: (limit) => api.get('/reports/top-products', { params: { limit } }),
  getServicePerformance: () => api.get('/reports/service-performance'),
  
  // Dashboard Financeiro endpoints
  getDashboardSummary: (params) => api.get('/reports/dashboard/summary', { params }),
  getDashboardAlerts: (params) => api.get('/reports/dashboard/alerts', { params }),
  getRecentActivities: (params) => api.get('/reports/dashboard/recent-activities', { params }),
  getSalesChart: (params) => api.get('/reports/dashboard/sales-chart', { params }),
  getExpensesChart: (params) => api.get('/reports/dashboard/expenses-chart', { params }),
  getCashByLocation: (params) => api.get('/reports/dashboard/cash-by-location', { params }),
  getTopSales: (params) => api.get('/reports/dashboard/top-sales', { params }),
  getComparison: (params) => api.get('/reports/dashboard/comparison', { params }),
};

// =============================================================================
// ATTACHMENTS API (P3 - Upload de Comprovantes)
// =============================================================================
export const attachmentsAPI = {
  // Upload
  upload: (formData) => api.post('/attachments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMultiple: (formData) => api.post('/attachments/upload-multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // List & Get
  listByEntity: (entityType, entityId, includeDeleted = false) => 
    api.get(`/attachments/by-entity/${entityType}/${entityId}`, { params: { include_deleted: includeDeleted } }),
  get: (id) => api.get(`/attachments/${id}`),
  fetchDownloadBlob: (id) => fetchBlobWithAuth(`/attachments/${id}/download`, 'Erro ao descarregar anexo'),
  fetchPreviewBlob: (id) => fetchBlobWithAuth(`/attachments/${id}/preview`, 'Erro ao carregar pré-visualização do anexo'),
  
  // Download & Preview URLs (uses /api prefix for proper routing)
  getDownloadUrl: (id) => `${API_URL}/attachments/${id}/download`,
  getPreviewUrl: (id) => `${API_URL}/attachments/${id}/preview`,

  openPreviewAuthenticated: async (id) => {
    const response = await fetchBlobWithAuth(`/attachments/${id}/preview`, 'Erro ao carregar pré-visualização do anexo');
    const blobUrl = URL.createObjectURL(response.data);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return { success: true };
  },

  downloadAuthenticated: async (id, filename = 'anexo') => {
    const response = await fetchBlobWithAuth(`/attachments/${id}/download`, 'Erro ao descarregar anexo');
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return { success: true };
  },
  
  // Delete (reason is REQUIRED for Admin/Manager)
  delete: (id, reason = null) => api.delete(`/attachments/${id}`, { data: { reason } }),
  
  // Audit
  getAuditLog: (id) => api.get(`/attachments/${id}/audit`),
};


// =============================================================================
// NOTIFICATIONS API (P4 - Push Notifications)
// =============================================================================
export const notificationsAPI = {
  // Subscription management
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribe: () => api.post('/notifications/unsubscribe'),
  getStatus: () => getCachedApiGet('notifications.status', () => api.get('/notifications/status'), 30000),
  
  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
  
  // Notifications
  getPending: () => api.get('/notifications/pending'),
  markSent: (id) => api.post(`/notifications/mark-sent/${id}`),
  dismiss: (id) => api.post(`/notifications/dismiss/${id}`),
};

// =============================================================================
// CLIENTS API - Gestão de Clientes
// =============================================================================
export const clientsAPI = {
  list: (params = {}) => api.get('/clients', { params }),
  listActive: (options = {}) => {
    const params = { ...(options || {}) };
    const compactSuffix = params.compact ? 'compact' : 'full';
    return getCachedApiGet(
      `clients.active.${compactSuffix}`,
      () => api.get('/clients/active', { params }),
      params.compact ? 60000 : 15000,
    );
  },
  getDefault: () => api.get('/clients/default'),
  downloadTemplate: () => api.get('/clients/import/template.xlsx', { responseType: 'blob' }),
  importExcel: (file, upsertByNuit = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/clients/import', formData, {
      params: { upsert_by_nuit: upsertByNuit },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkStatus: (data) => api.post('/clients/bulk-status', data),
  hardDeleteBulk: (data) => api.post('/clients/hard-delete-bulk', data),
  search: (q, limit = 10) => api.get('/clients/search', { params: { q, limit } }),
  get: (id) => api.get(`/clients/${id}`),
  getHistory: (id, limit = 50) => api.get(`/clients/${id}/history`, { params: { limit } }),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id, deleteReason) => api.delete(`/clients/${id}`, { data: { delete_reason: deleteReason } }),
  restore: (id) => api.post(`/clients/${id}/restore`),
};

// =============================================================================
// CONSOLES API - Gestão de Consolas Gaming
// =============================================================================
export const consolesAPI = {
  list: (params = {}) => api.get('/consoles', { params }),
  getByLocation: (locationId) => api.get(`/consoles/location/${locationId}`),
  get: (id) => api.get(`/consoles/${id}`),
  create: (data) => api.post('/consoles', data),
  update: (id, data) => api.put(`/consoles/${id}`, data),
  delete: (id, deleteReason) => api.delete(`/consoles/${id}`, { data: { delete_reason: deleteReason } }),
  restore: (id) => api.post(`/consoles/${id}/restore`),
  initializeLocation: (locationId) => api.post(`/consoles/location/${locationId}/initialize`),
};


// =============================================================================
// LICENSES API - Gestão de Licenças e Alertas - P0.3 Extended
// =============================================================================
export const licensesAPI = {
  // License types
  getTypes: () => api.get('/licenses/types'),
  listTypes: (category) => api.get('/licenses/types', { params: { category } }),
  getCategories: () => api.get('/licenses/types/categories'),
  listCategories: () => api.get('/licenses/types/categories'),
  createType: (data) => api.post('/licenses/types', data),
  updateType: (id, data) => api.put(`/licenses/types/${id}`, data),
  deleteType: (id) => api.delete(`/licenses/types/${id}`),
  seedTypes: () => api.post('/licenses/seed-types'),
  
  // Alert options
  listAlertOptions: () => api.get('/licenses/alert-options'),
  createAlertOption: (data) => api.post('/licenses/alert-options', data),
  
  // Client licenses - P0.3 Updated
  list: (params = {}) => api.get('/licenses', { params }),
  getExpiring: (days = 30) => api.get('/licenses/expiring', { params: { days } }),
  getClientLicenses: (clientId) => api.get(`/licenses/client/${clientId}`),
  getClientPendingActions: (clientId) => api.get(`/licenses/client/${clientId}/pending-actions`),
  create: (data) => api.post('/licenses', data),
  update: (id, data) => api.put(`/licenses/${id}`, data),
  delete: (id) => api.delete(`/licenses/${id}`),
  renew: (id, newEndDate, notes) => api.post(`/licenses/${id}/renew`, null, { 
    params: { new_end_date: newEndDate, notes } 
  }),
  getRenewals: (id) => api.get(`/licenses/${id}/renewals`),
  
  // Alerts - Enhanced Dashboard
  getPendingAlerts: () => api.get('/licenses/alerts/pending'),
  getAlertsDashboard: (params = {}) => api.get('/licenses/alerts/dashboard', { params }),
  markAlertAction: (data) => api.post('/licenses/alerts/action', data),
  bulkMarkAlerts: (data) => api.post('/licenses/alerts/bulk-action', data),
  getAlertHistory: (alertId) => api.get(`/licenses/alerts/${alertId}/history`),
  getWarrantyAlerts: (days = 30) => api.get('/licenses/warranty-alerts', { params: { days } }),
  markAlertSent: (id) => api.post(`/licenses/alerts/${id}/mark-sent`),
};

// =============================================================================
// PRODUCT SPECS API - Especificações de Produtos por Categoria
// =============================================================================
export const specsAPI = {
  list: (category) => api.get('/catalogs/spec-definitions', { params: { category } }),
  getByCategory: (category) => api.get(`/catalogs/spec-definitions/by-category/${category}`),
  listCategories: () => api.get('/catalogs/spec-definitions/categories'),
  create: (data) => api.post('/catalogs/spec-definitions', data),
  update: (id, data) => api.put(`/catalogs/spec-definitions/${id}`, data),
  delete: (id) => api.delete(`/catalogs/spec-definitions/${id}`),
};

// =============================================================================
// INTEGRITY & ANTI-FRAUD API - Verificação de Integridade do Sistema
// =============================================================================
export const integrityAPI = {
  // Run full integrity check
  runCheck: () => api.get('/integrity/check'),
  
  // Get check history
  getHistory: (limit = 10) => api.get('/integrity/history', { params: { limit } }),
  
  // Get suspicious activities
  getSuspiciousActivities: (days = 7) => api.get('/integrity/suspicious-activities', { params: { days } }),
  
  // Get audit summary
  getAuditSummary: (days = 30) => api.get('/integrity/audit-summary', { params: { days } }),

  // Premium PDF for audit summary
  exportAuditSummaryPdf: (days = 30) => api.get('/integrity/audit-summary/pdf', {
    params: { days },
    responseType: 'blob'
  }),
  
  // Get issue details
  getIssueDetails: (issueType, skip = 0, limit = 100) => 
    api.get(`/integrity/issue-details/${issueType}`, { params: { skip, limit } }),
  
  // Export issue report
  exportIssue: (issueType, format = 'excel') => 
    api.get(`/integrity/export/${issueType}`, { 
      params: { format },
      responseType: 'blob'
    }),
};


// =============================================================================
// FEEDBACK API - P1-FEEDBACK-01
// =============================================================================
export const feedbackAPI = {
  createTicket: (data) => api.post('/feedback/tickets', data),
  listTickets: (params = {}) => api.get('/feedback/tickets', { params }),
  getTicket: (id) => api.get(`/feedback/tickets/${id}`),
  updateTicket: (id, data) => api.put(`/feedback/tickets/${id}`, data),
  updateStatus: (id, data) => api.post(`/feedback/tickets/${id}/status`, data),
  cancelTicket: (id, data = {}) => api.post(`/feedback/tickets/${id}/cancel`, data),
  deleteTicket: (id) => api.delete(`/feedback/tickets/${id}`),
  addComment: (id, data) => api.post(`/feedback/tickets/${id}/comment`, data),
  getStats: () => getCachedApiGet('feedback.stats', () => api.get('/feedback/stats'), 60000),
};


